import { getParticipantColor } from '@atlaskit/editor-shared-styles/utils';
import { token } from '@atlaskit/tokens';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

/**
 * Which badge treatment the telepointer renders for an agent.
 *
 * `agent` is a first-party agent (Studio / OOTB / system): an id-derived accent pill with the
 * avatar and no name. `agent-third-party` is a Forge / third-party / remote agent: the neutral
 * grey pill with the avatar and the agent's name.
 */
export type TelepointerAgentKind = 'agent' | 'agent-third-party' | 'fallback' | 'rovo';

/** The agent identifiers shared avatar resolution keys on. */
export interface TelepointerAgentIdentity {
	/**
	 * The agent's own id in its source system (e.g. Rovo's fixed agent UUID). Distinct from
	 * `agentIdentityAccountId`: this identifies *which agent*, not an Atlassian identity.
	 */
	agentId?: string;
	/** The Atlassian identity-service account id provisioned for the agent, if any (used to fetch/generate its avatar). Distinct from `agentId`. */
	agentIdentityAccountId?: string | null;
	agentName?: string;
	agentNamedId?: string | null;
	/**
	 * Forge / third-party / remote agent, derived on the producer side from the agent record's
	 * `creator_type`. Third parties get the neutral grey pill and their name; first parties get the
	 * id-derived accent pill and no name.
	 */
	isThirdParty?: boolean;
}

/**
 * The colours the telepointer paints for an agent. Badge, cursor line and content highlight
 * take one hue, so a producer sets all three from this single result.
 */
export interface TelepointerPresentation {
	highlightBackgroundColor: string;
	highlightBorderColor: string;
	telepointerAgentKind: TelepointerAgentKind;
	telepointerLineColor: string;
}

/**
 * The neutral grey the fallback badge paints, shared with the third-party pill. For the initial
 * PR a third party takes the same grey as the fallback (differing only by keeping its avatar and
 * name); per-brand third-party colours are a follow-up.
 */
const NEUTRAL_GRAY: Omit<TelepointerPresentation, 'telepointerAgentKind'> = {
	highlightBackgroundColor: token('color.background.accent.gray.subtlest'),
	highlightBorderColor: token('color.border.accent.gray'),
	// A background token, because this paints the badge fill and the cursor line.
	telepointerLineColor: token('color.background.accent.gray.subtler'),
};

/**
 * Resolves how the telepointer badge should present a given agent.
 *
 * Deliberately reads no experiment: the caller decides whether the redesign applies, so this
 * stays a pure function of the agent's identity and can be shared by every producer.
 *
 * A first party takes an id-derived accent pill; a third party (`isThirdParty`) takes the neutral
 * grey pill. The badge itself decides whether to show the name (third party only).
 */
export const resolveTelepointerPresentation = (
	identity?: TelepointerAgentIdentity,
): TelepointerPresentation => {
	// Rovo always presents as itself, so this badge is fixed rather than identity driven.
	if (identity && isDefaultRovoChat(identity)) {
		return {
			highlightBackgroundColor: token('color.background.accent.purple.subtlest'),
			highlightBorderColor: token('color.border.accent.purple'),
			telepointerAgentKind: 'rovo',
			telepointerLineColor: token('color.rovo.background.brand.bold'),
		};
	}

	const { agentId, agentIdentityAccountId, agentNamedId, isThirdParty } = identity ?? {};

	// Any one of the three identifiers shared agent-avatar resolution keys on is enough to
	// draw an avatar, which leaves the neutral badge for a complete absence of all of them.
	if (agentNamedId || agentIdentityAccountId || agentId) {
		// A third party keeps the neutral grey pill (plus its avatar and name); only the first
		// party takes an id-derived accent hue.
		if (isThirdParty) {
			return { ...NEUTRAL_GRAY, telepointerAgentKind: 'agent-third-party' };
		}

		// Hash the identity to one of the shared participant colours — the same palette (and the
		// same `getParticipantColor` function) that colours human collaboration cursors, and that
		// the show-diff attribution system (version history / suggestions) hashes step attribution
		// onto. This keeps one agent's colour consistent everywhere it is attributed, rather than
		// matching only that agent's own avatar illustration.
		const { color } = getParticipantColor(agentId || agentNamedId || agentIdentityAccountId || '');

		return {
			highlightBackgroundColor: color.svgBackgroundColor,
			highlightBorderColor: color.backgroundColor,
			telepointerAgentKind: 'agent',
			telepointerLineColor: color.backgroundColor,
		};
	}

	return { ...NEUTRAL_GRAY, telepointerAgentKind: 'fallback' };
};
